import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import type { OrgHelper } from "@propelauth/react";

export const ENABLE_ORG_CHECK = true;

export type OrgState = "checking" | "resolved" | "picking";

export type JoinableOrg = { id: string; name: string };

type CaptureEventFn = (eventName: string, properties?: Record<string, unknown>) => void;

export const useOrgCheck = ({
  isLoggedIn,
  orgHelper,
  skip = false,
  captureEvent,
}: {
  isLoggedIn: boolean;
  orgHelper?: OrgHelper | null;
  skip?: boolean;
  captureEvent?: CaptureEventFn;
}) => {
  const { fetchJoinableOrgs, joinOrg } = useAuthFrontendApis();
  const orgCheckRef = useRef(false);
  const [orgState, setOrgState] = useState<OrgState>("checking");
  const [joinableOrgs, setJoinableOrgs] = useState<JoinableOrg[]>([]);

  const fireEvent = useCallback(
    (outcome: string, properties?: Record<string, unknown>) => {
      captureEvent?.("org_check_result", { outcome, ...properties });
    },
    [captureEvent],
  );

  useEffect(() => {
    if (orgCheckRef.current) return;

    if (!ENABLE_ORG_CHECK || skip || !isLoggedIn) {
      setOrgState("resolved");
      return;
    }

    const existingOrgs = orgHelper?.getOrgs() ?? [];
    if (existingOrgs.length > 0) {
      fireEvent("already_has_org", { org_count: existingOrgs.length });
      setOrgState("resolved");
      return;
    }

    orgCheckRef.current = true;

    fetchJoinableOrgs().then((response) => {
      response.handle({
        success(data) {
          if (data.orgs.length === 1) {
            fireEvent("auto_joined", { org_name: data.orgs[0].name });
            joinOrg(data.orgs[0].id).then((r) =>
              r.handle({
                success() {
                  window.location.reload();
                },
                unexpectedOrUnhandled() {
                  setOrgState("resolved");
                },
              }),
            );
          } else if (data.orgs.length > 1) {
            fireEvent("picker_shown", {
              org_count: data.orgs.length,
              org_names: data.orgs.map((o) => o.name),
            });
            setJoinableOrgs(data.orgs);
            setOrgState("picking");
          } else {
            fireEvent("no_joinable_orgs");
            setOrgState("resolved");
          }
        },
        orgsNotEnabled() {
          fireEvent("orgs_not_enabled");
          setOrgState("resolved");
        },
        unexpectedOrUnhandled() {
          fireEvent("error");
          setOrgState("resolved");
        },
      });
    });
  }, [isLoggedIn, skip, orgHelper, fetchJoinableOrgs, joinOrg, fireEvent]);

  const handleJoinOrg = (orgId: string) => {
    const org = joinableOrgs.find((o) => o.id === orgId);
    fireEvent("picker_org_selected", { org_name: org?.name });
    joinOrg(orgId).then((r) =>
      r.handle({
        success() {
          window.location.reload();
        },
        unexpectedOrUnhandled(err) {
          console.error("Failed to join org", err);
        },
      }),
    );
  };

  return { orgState, joinableOrgs, joinOrg: handleJoinOrg };
};
